import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  CloseIcon,
  DatePicker,
  DragDropFileListLocal,
  Drawer,
  Input,
  InputNumber,
  Label,
  Select,
  showToast,
  Text,
} from '@mercai/clever'

import { objectToFormData, useValidationErrors } from '@/helpers'
import { QUERIES_ADMIN, useShipmentInvoiceCreate } from '@/services'
import {
  LogisticShipmentProps,
  SaleOrderProps,
  ShipmentInvoicePaymentPriorityEnum,
  ShipmentInvoiceProps,
  ShipmentInvoiceTypeEnum,
  TransportCompanyProps,
} from '@/types'

import { useQueryClient } from '@tanstack/react-query'

import { ShipmentInvoiceDrawerRequestNewProps } from './drawer-request-new.type'

import { SaleOrderSearch, TransportCompanySearch } from '@/components'
import { schema } from './drawer-request-new.constants'
import styles from './drawer-request-new.module.css'

const initialValues: Partial<ShipmentInvoiceProps> = {
  currency: 'MXN',
}

export const ShipmentInvoiceDrawerRequestNew = ({
  onHandleOpen,
  isOpen,
}: ShipmentInvoiceDrawerRequestNewProps) => {
  const { t: tEnums } = useTranslation('enums')
  const { t } = useTranslation('screenSupplierOrderManagementPage')

  const [values, setValues] =
    useState<Partial<ShipmentInvoiceProps>>(initialValues)

  const [salesOrderShpments, setSalesOrderShipments] = useState<
    LogisticShipmentProps[]
  >([])
  const [invoicePdfFile, setInvoicePdfFile] = useState<File[]>([])
  const [invoiceXmlFile, setInvoiceXmlFile] = useState<File[]>([])

  const { mutateAsync, isPending } = useShipmentInvoiceCreate()

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schema(t),
    values,
  )

  const queryClient = useQueryClient()

  const onSelectSaleOrders = (saleOrderParam: SaleOrderProps) => {
    const isSelected = salesOrderShpments.find(
      (saleOrder) => saleOrder.order === saleOrderParam.reference,
    )
    if (!isSelected) {
      setSalesOrderShipments([
        ...salesOrderShpments,
        saleOrderParam.shipments?.[0],
      ])
    }
  }

  const onRemoveSaleOrders = (saleOrderReference: string) => {
    const isSelected = salesOrderShpments.find(
      (saleOrder) => saleOrder.order === saleOrderReference,
    )
    if (isSelected) {
      setSalesOrderShipments(
        salesOrderShpments.filter((s) => s.order !== saleOrderReference),
      )
    }
  }

  const onHandleValues = (key: string, value?: string | number | null) => {
    setValues({ ...values, [key]: value })
  }

  const onClose = () => {
    onHandleOpen()
    setValues(initialValues)
    setInvoicePdfFile([])
    setInvoiceXmlFile([])
    setSalesOrderShipments([])
  }

  const onSubmit = async () => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }
    onHandleListenErrors(false)

    const payload = objectToFormData({
      ...values,
      transportCompany: undefined,
      shipments: undefined,
      shipmentsUuid:
        salesOrderShpments?.length > 0
          ? salesOrderShpments.map((s) => s.uuid)
          : undefined,
    })

    if (invoicePdfFile.length > 0) {
      payload.append('invoicePdfFile', invoicePdfFile[0] || '')
    }

    if (invoiceXmlFile.length > 0) {
      payload.append('invoiceXmlFile', invoiceXmlFile[0] || '')
    }

    const response = await mutateAsync(payload)

    if (response.success) {
      onClose()

      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.BFF_SHIPMENT_INVOICE.module],
      })

      showToast({
        position: 'bottom-left',
        variant: 'success',
        title: t(
          'tabShipmentInvoiceDrawerRequestNewShipmentInvoiceSuccessTitle',
        ),
        content: t(
          'tabShipmentInvoiceDrawerRequestNewShipmentInvoiceSuccessDescription',
        ),
      })
      return null
    }

    showToast({
      position: 'bottom-left',
      variant: 'error',
      content:
        response.message ||
        t('tabShipmentInvoiceDrawerRequestNewShipmentInvoiceErrorDescription'),
    })
  }

  return (
    <Drawer isOpen={!!isOpen} onHandleOpen={onClose} className={styles.drawer}>
      <div className={styles.header}>
        <div>
          <Text variant="title-large" className={styles['header-title']}>
            {t('tabShipmentInvoiceDrawerRequestNewShipmentInvoiceTitle')}
          </Text>

          <Text variant="text-small" className={styles['header-description']}>
            {t('tabShipmentInvoiceDrawerRequestNewShipmentInvoiceDescription')}
          </Text>
        </div>

        <button
          className={styles['header-button']}
          type="button"
          onClick={onHandleOpen}
        >
          <CloseIcon />
        </button>
      </div>

      <div className={styles.content}>
        <div>
          <Label required>
            {t(
              'tabShipmentInvoiceDrawerRequestNewShipmentInvoiceTransportCompanyLabel',
            )}
          </Label>
          <TransportCompanySearch
            title={' '}
            selected={values?.transportCompany}
            onSelect={(transportCompany) =>
              setValues({
                ...values,
                transportCompany: transportCompany as TransportCompanyProps,
                transportCompanyUuid: transportCompany?.uuid,
              })
            }
            error={errors?.transportCompanyUuid as string}
          />
        </div>

        <div>
          <Label required>
            {t('tabShipmentInvoiceDrawerRequestNewShipmentInvoiceTypeLabel')}
          </Label>
          <Select
            allowEmpty={false}
            value={values?.type}
            options={Object.keys(ShipmentInvoiceTypeEnum)
              .filter((val) => val !== 'unknown')
              .map((key) => ({
                label: tEnums(`shipment_invoice_type_${key}`),
                value: key,
              }))}
            onChange={(type) => onHandleValues('type', type)}
            error={errors?.type as string}
          />
        </div>

        <div>
          <Label>
            {t(
              'tabShipmentInvoiceDrawerRequestNewShipmentInvoicePaymentPriorityLabel',
            )}
          </Label>
          <Select
            allowEmpty={false}
            value={values?.paymentPriority}
            options={Object.keys(ShipmentInvoicePaymentPriorityEnum).map(
              (key) => ({
                label: tEnums(`shipment_invoice_payment_priority_${key}`),
                value: key,
              }),
            )}
            onChange={(paymentPriority) =>
              onHandleValues('paymentPriority', paymentPriority)
            }
          />
        </div>

        <div>
          <Label required>
            {t('tabShipmentInvoiceDrawerRequestNewShipmentInvoiceValueLabel')}
          </Label>
          <div className={styles.row}>
            <Select
              allowEmpty={false}
              value={values.currency}
              options={[
                { label: 'USD', value: 'USD' },
                { label: 'MXN', value: 'MXN' },
              ]}
              onChange={(currency) => onHandleValues('currency', currency)}
            />
            <div style={{ flex: 1 }}>
              <InputNumber
                value={values?.value}
                decimalPrecision={2}
                onChange={(value) => onHandleValues('value', value)}
                showIcons
                variant={errors?.value ? 'danger' : 'primary'}
              />
            </div>
          </div>
          {errors?.value && (
            <Text className={styles.error}>{errors.value as string}</Text>
          )}
        </div>

        <div>
          <Label>
            {t(
              'tabShipmentInvoiceDrawerRequestNewShipmentInvoiceSaleOrdersLabel',
            )}
          </Label>
          <div>
            <SaleOrderSearch
              title={' '}
              onSelect={(value) => onSelectSaleOrders(value as SaleOrderProps)}
            />
            <div className={styles['sale-orders']}>
              {salesOrderShpments.map((saleOrder) => (
                <div className={styles['sale-order']} key={saleOrder.reference}>
                  <Text variant="text-small">{saleOrder.order}</Text>
                  <CloseIcon
                    className={styles['sale-order-remove-icon']}
                    onClick={() => onRemoveSaleOrders(saleOrder.order)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <Label>
            {t(
              'tabShipmentInvoiceDrawerRequestNewShipmentInvoiceExpectedPaymentDateLabel',
            )}
          </Label>
          <DatePicker
            value={values?.expectedPaymentDate}
            onChange={(expectedPaymentDate) =>
              onHandleValues('expectedPaymentDate', expectedPaymentDate)
            }
            placeholder="DD/MM/AAAA"
          />
        </div>

        <div>
          <Label>
            {t(
              'tabShipmentInvoiceDrawerRequestNewShipmentInvoiceObservationLabel',
            )}
          </Label>
          <Input
            value={values?.observation}
            onChange={(observation) =>
              onHandleValues('observation', observation)
            }
          />
        </div>

        <div>
          <Label>
            {t(
              'tabShipmentInvoiceDrawerRequestNewShipmentInvoiceInvoiceExternalReferenceLabel',
            )}
          </Label>
          <Input
            value={values?.invoiceExternalReference}
            onChange={(invoiceExternalReference) =>
              onHandleValues(
                'invoiceExternalReference',
                invoiceExternalReference,
              )
            }
          />
        </div>

        <div>
          <Label>
            {t(
              'tabShipmentInvoiceDrawerRequestNewShipmentInvoiceInvoicePdfFileLabel',
            )}
          </Label>
          <DragDropFileListLocal
            translations={{
              dragFile: t('uploadDragFile'),
              clickToUpload: t('uploadClickToUpload'),
              sizeFile: t('uploadSizeFile'),
            }}
            data={invoicePdfFile}
            onDrop={(file) => setInvoicePdfFile(file)}
            onRemove={(file) => setInvoicePdfFile(file)}
          />
        </div>

        <div>
          <Label>
            {t(
              'tabShipmentInvoiceDrawerRequestNewShipmentInvoiceInvoiceXmlFileLabel',
            )}
          </Label>
          <DragDropFileListLocal
            translations={{
              dragFile: t('uploadDragFile'),
              clickToUpload: t('uploadClickToUpload'),
              sizeFile: t('uploadSizeFile'),
            }}
            data={invoiceXmlFile}
            onDrop={(file) => setInvoiceXmlFile(file)}
            onRemove={(file) => setInvoiceXmlFile(file)}
          />
        </div>
      </div>

      <div className={styles.footer}>
        <Button variant="secondary" onClick={onClose}>
          {t('tableSupplierTransactionDrawerEditCancelButton')}
        </Button>
        <Button onClick={onSubmit} disabled={isPending} isActive={!isPending}>
          {t('tableSupplierTransactionDrawerEditConfirmButton')}
        </Button>
      </div>
    </Drawer>
  )
}
